// „KI im Gespräch“ in der Admin-Übersicht (seit 24.10.2026).
//
// Anlass: Der Nutzer fragte, ob das Thinking-Modell benutzt wird (nein - es
// fiel am 19.09.2026 durch), und wollte es ausprobieren lassen. Hier: welches
// Modell die Gesprächsrunde im Vertragsstudio nutzt, Umschalten mit einem Klick
// (gilt nur für die Gesprächsrunde, nicht für Abwägen, Einordnung, Bilder) und
// der Vergleich beider Modelle an denselben Fällen - auf dem Server mit dessen
// Schlüssel, ein Fall je Anfrage (api/_lib/modellVergleich.js).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Cpu, Play, Square } from 'lucide-react';
import { T, knopfStil } from '../../lib/designTokens';
import {
  DENKMODELL, STANDARD, empfehlung, ergebnisAlsText, modellName, pruefungsTabelle, vergleichsPlan, zusammenfassung,
} from '../../lib/modellVergleich';

const QUELLE_TEXT = {
  vorgabe: 'Vorgabe',
  uebersicht: 'hier gewählt',
  umgebung: 'fest per Vercel-Einstellung VIBECODE_MODELL_GESPRAECH – hier nicht umschaltbar',
};

const MODELLE = [STANDARD, DENKMODELL];

const zelle = { padding: '6px 8px', borderTop: `1px solid ${T.hairline}`, fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap' };

export default function KiModellVergleich({ apiBase = '/api', session = null }) {
  const [stand, setStand] = useState(null);
  const [fehler, setFehler] = useState('');
  const [schaltet, setSchaltet] = useState(false);
  const [runden, setRunden] = useState(2);
  const [lauf, setLauf] = useState(null); // { fertig, gesamt, aktuell }
  const [ergebnisse, setErgebnisse] = useState([]);
  const anhalten = useRef(false);

  const kopf = useMemo(() => (
    session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}
  ), [session]);
  const adresse = `${apiBase}/contracts/state?resource=ki-modell`;

  const laden = useCallback(async () => {
    try {
      const antwort = await axios.get(adresse, { headers: kopf });
      setStand(antwort.data);
      setFehler('');
    } catch (error) {
      setFehler(error?.response?.data?.detail || error.message || 'Nicht ladbar.');
    }
  }, [adresse, kopf]);

  useEffect(() => { laden(); }, [laden]);

  const waehlen = useCallback(async (modell) => {
    if (modell === DENKMODELL && !window.confirm(
      'Das Denkmodell gilt dann für alle Gesprächsrunden im Vertragsstudio (spätestens nach 15 Sekunden überall). '
      + 'Abwägen, Einordnung und Bilder bleiben beim Standard. Zurück geht es hier mit einem Klick.',
    )) return;
    setSchaltet(true);
    try {
      const antwort = await axios.post(adresse, { op: 'modell', modell: modell === STANDARD ? '' : modell }, { headers: kopf });
      setStand((s) => ({ ...s, ...antwort.data }));
      toast.success(`Im Gespräch gilt jetzt: ${modellName(antwort.data.modell)}.`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message || 'Nicht gespeichert.');
    } finally {
      setSchaltet(false);
    }
  }, [adresse, kopf]);

  const faelle = useMemo(() => stand?.faelle || [], [stand]);

  const vergleichen = useCallback(async () => {
    const plan = vergleichsPlan(faelle, MODELLE, runden);
    anhalten.current = false;
    setErgebnisse([]);
    const gesammelt = [];
    for (let i = 0; i < plan.length; i += 1) {
      if (anhalten.current) break;
      const schritt = plan[i];
      setLauf({ fertig: i, gesamt: plan.length, aktuell: schritt });
      try {
        // Ein Fall darf so lange dauern wie die Funktion (300 s).
        const antwort = await axios.post(adresse, { op: 'vergleich', fall: schritt.fall, modell: schritt.modell }, { headers: kopf, timeout: 305000 });
        gesammelt.push({ ...antwort.data, runde: schritt.runde });
      } catch (error) {
        gesammelt.push({
          ...schritt,
          fehler: error?.code === 'ECONNABORTED' ? 'ZEIT' : (error?.response?.status ? `HTTP_${error.response.status}` : 'NETZ'),
          meldung: error?.response?.data?.detail || error.message || '',
          pruefungen: [],
        });
      }
      setErgebnisse([...gesammelt]);
    }
    setLauf(null);
    toast.success(anhalten.current ? 'Vergleich angehalten.' : 'Vergleich fertig.');
  }, [adresse, faelle, kopf, runden]);

  const zusammen = useMemo(() => zusammenfassung(ergebnisse, MODELLE), [ergebnisse]);
  const tabelle = useMemo(() => pruefungsTabelle(faelle, ergebnisse, MODELLE), [faelle, ergebnisse]);
  const rat = useMemo(() => (lauf ? null : empfehlung(zusammen)), [lauf, zusammen]);

  const kopieren = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(ergebnisAlsText({ faelle, ergebnisse, modelle: MODELLE }));
      toast.success('Ergebnis kopiert.');
    } catch (_) {
      toast.error('Kopieren ging nicht – der Browser hat es verweigert.');
    }
  }, [faelle, ergebnisse]);

  if (fehler) return <div style={{ fontSize: 13, color: T.gefahr }}>KI-Einstellung nicht ladbar: {fehler}</div>;
  if (!stand) return <div style={{ fontSize: 13, color: T.muted }}>Lädt …</div>;

  const fest = stand.quelle === 'umgebung';
  const dauer = runden * faelle.length * 2;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Cpu size={16} color={T.navy} />
        <span style={{ fontSize: 13, color: T.body }}>
          Die Gesprächsrunde im Vertragsstudio nutzt <strong style={{ color: T.navy }}>{modellName(stand.modell)}</strong>
          {' '}<span style={{ color: T.muted }}>({stand.modell} · {QUELLE_TEXT[stand.quelle] || stand.quelle}
          {stand.geaendertVon && stand.quelle === 'uebersicht' ? ` von ${stand.geaendertVon}` : ''})</span>
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {MODELLE.map((modell) => (
          <button key={modell} type="button" disabled={fest || schaltet || stand.modell === modell}
            onClick={() => waehlen(modell)}
            style={{ ...knopfStil(stand.modell === modell), opacity: fest || schaltet ? 0.5 : 1 }}>
            {modellName(modell)}{stand.modell === modell ? ' (aktiv)' : ''}
          </button>
        ))}
      </div>

      <div style={{ borderTop: `1px solid ${T.hairline}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 12.5, color: T.body, lineHeight: 1.6 }}>
          <strong>Vergleich:</strong> Beide Modelle bearbeiten dieselben {faelle.length} Fälle durch die echte Gesprächsrunde –
          unter anderem Pacht 50:50 aus Deal 12022 mit 14 Tagen Zahlungsziel. Gezählt werden Abbrüche, Dauer und erfüllte
          Prüfungen; die Texte stehen darunter zum Vergleichen. {dauer} Läufe, mit dem Denkmodell einige Minuten. Es wird
          nichts gespeichert und kein Vertrag angefasst.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12.5, color: T.body }}>
            Runden{' '}
            <select value={runden} disabled={Boolean(lauf)} onChange={(e) => setRunden(Number(e.target.value))}
              style={{ marginLeft: 4, padding: '4px 6px', borderRadius: 6, border: `1px solid ${T.rand}` }}>
              {[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          {lauf ? (
            <button type="button" onClick={() => { anhalten.current = true; toast.info('Hält nach diesem Fall an.'); }} style={knopfStil()}>
              <Square size={12} /> Anhalten
            </button>
          ) : (
            <button type="button" disabled={!stand.kiBereit || !faelle.length} onClick={vergleichen}
              style={{ ...knopfStil(true), opacity: stand.kiBereit ? 1 : 0.5 }}>
              <Play size={12} /> Vergleich starten
            </button>
          )}
          {ergebnisse.length && !lauf ? (
            <button type="button" onClick={kopieren} style={knopfStil()}>
              <Copy size={12} /> Ergebnis kopieren
            </button>
          ) : null}
          {lauf ? (
            <span style={{ fontSize: 12.5, color: T.muted }}>
              {lauf.fertig + 1} von {lauf.gesamt} · {lauf.aktuell.fall} · {modellName(lauf.aktuell.modell)}
            </span>
          ) : null}
        </div>
        {!stand.kiBereit ? <div style={{ fontSize: 12.5, color: T.gefahr }}>Kein Vibecode-Schlüssel hinterlegt.</div> : null}
      </div>

      {ergebnisse.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rat ? (
            <div style={{
              padding: '9px 12px', borderRadius: T.rChip, fontSize: 12.5, lineHeight: 1.55,
              background: rat.ton === 'gut' ? T.gutGrund : T.warnGrund,
              borderLeft: `3px solid ${rat.ton === 'gut' ? T.gut : T.warn}`, color: T.ink,
            }}>{rat.text}</div>
          ) : null}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 520 }}>
              <thead>
                <tr>
                  {['Modell', 'Läufe', 'Abbrüche', 'Ø s/Fall', 'max s', 'Ø Token', 'Prüfungen'].map((k, i) => (
                    <th key={k} style={{ ...zelle, borderTop: 'none', textAlign: i ? 'right' : 'left', color: T.muted, fontWeight: 700 }}>{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {zusammen.map((z) => (
                  <tr key={z.modell}>
                    <td style={{ ...zelle, textAlign: 'left', fontWeight: 700, color: T.navy }}>{modellName(z.modell)}</td>
                    <td style={zelle}>{z.laeufe}</td>
                    <td style={{ ...zelle, color: z.fehler ? T.gefahr : T.body }} title={Object.entries(z.fehlerArten).map(([a, n]) => `${a} ×${n}`).join(', ')}>{z.fehler}</td>
                    <td style={zelle}>{z.schnittS.toFixed(1)}</td>
                    <td style={zelle}>{Math.round(z.maxS)}</td>
                    <td style={zelle}>{z.schnittTokenAus}</td>
                    <td style={{ ...zelle, fontWeight: 700 }}>{z.erfuellt}/{z.geprueft}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {tabelle.map((f) => (
            <details key={f.fall} style={{ border: `1px solid ${T.randWeich}`, borderRadius: 10, padding: '8px 12px', background: '#fff' }}>
              <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 700, color: T.navy }}>
                {f.beschreibung}
                <span style={{ fontWeight: 500, color: T.muted }}>
                  {' · '}{f.zeilen.filter((z) => !z.info).map((z) => z.je.map((j) => `${j.erfuellt}/${j.von}`).join(' : ')).join('  ')}
                </span>
              </summary>
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {f.zeilen.map((z) => (
                  <div key={z.name} style={{ fontSize: 12, color: T.body }}>
                    {z.info ? <em>(Info) </em> : null}{z.name}:{' '}
                    {z.je.map((j) => `${modellName(j.modell)} ${j.erfuellt}/${j.von}`).join(' · ')}
                  </div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10, marginTop: 10 }}>
                {MODELLE.map((modell) => {
                  const e = ergebnisse.find((x) => x.fall === f.fall && x.modell === modell && !x.fehler);
                  const fehlgeschlagen = ergebnisse.filter((x) => x.fall === f.fall && x.modell === modell && x.fehler);
                  return (
                    <div key={modell} style={{ background: T.grund, borderRadius: 8, padding: '8px 10px', fontSize: 12, lineHeight: 1.55 }}>
                      <div style={{ fontWeight: 700, color: T.navy, marginBottom: 4 }}>{modellName(modell)}</div>
                      {fehlgeschlagen.map((x, i) => (
                        <div key={`f${i}`} style={{ color: T.gefahr }}>Abbruch: {x.fehler} {x.meldung}</div>
                      ))}
                      {e ? (
                        <>
                          {(e.antworten || []).map((a, i) => <div key={`a${i}`} style={{ color: T.body }}>KI: {a}</div>)}
                          {(e.ersetzungen || []).map((a, i) => (
                            <div key={`e${i}`} style={{ marginTop: 4 }}>
                              <span style={{ color: T.gefahr, textDecoration: 'line-through' }}>{a.alt}</span>
                              {' → '}<span style={{ color: T.gut }}>{a.neu || '(streichen)'}</span>
                            </div>
                          ))}
                          {(e.ergaenzungen || []).map((p, i) => (
                            <div key={`p${i}`} style={{ marginTop: 4 }}><strong>{p.titel}:</strong> {p.text}</div>
                          ))}
                        </>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </details>
          ))}
        </div>
      ) : null}
    </div>
  );
}
